<template>
  <div class="chat-private-side">
    <!-- 好友信息展示区域 -->
    <div class="friend-info-section" v-if="userInfo">
      <!-- 好友基本信息卡片 -->
      <div class="friend-card">
        <div class="friend-avatar">
          <head-image 
            :size="80" 
            :url="userInfo.headImageThumb" 
            radius="50%" 
            :name="userInfo.nickName"
          />
        </div>

        <div class="friend-details">
          <div class="friend-name-row">
            <h4 class="friend-name" :title="userInfo.nickName">{{ userInfo.nickName }}</h4>
            <div class="gender-icons" v-if="userInfo.sex !== undefined">
              <i v-if="userInfo.sex === 0" class="el-icon-male gender-icon male" title="男"></i>
              <i v-else-if="userInfo.sex === 1" class="el-icon-female gender-icon female" title="女"></i>
            </div>
          </div>
          
          <div class="friend-company-row company-tag" v-if="userInfo.companyName">
            @{{ userInfo.companyName }}
          </div>

          <div class="friend-meta-list">
            <div class="meta-item">
              <span class="meta-label">ID:</span>
              <span class="meta-value">{{ userInfo.chatNo || '-' }}</span>
              <el-tooltip content="复制用户ID" placement="top" :open-delay="300">
                <i class="el-icon-copy-document copy-btn" @click="copyUserName"></i>
              </el-tooltip>
            </div>
            
            <div class="meta-item">
              <span class="meta-label">登录IP:</span>
              <span class="meta-value">{{ userInfo.lastLoginIp || '-' }}</span>
            </div>

            <div class="meta-item">
              <span class="meta-label">登录地区:</span>
              <span class="meta-value">{{ userInfo.lastLoginAddress || '-' }}</span>
            </div>

            <div class="meta-item">
              <span class="meta-label">登录时间:</span>
              <span class="meta-value">{{ userInfo.lastLoginTime || '-' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 个人设置区域 -->
      <div class="personal-setting">
        <div class="switch-item">
          <div class="label">
            <i class="el-icon-bell"></i>
            <span>消息免打扰</span>
          </div>
          <el-switch v-model="isDnd" @change="onDndChange" :disabled="loading.dnd"></el-switch>
        </div>
        <div class="switch-item">
          <div class="label">
            <i class="el-icon-top"></i>
            <span>置顶聊天</span>
          </div>
          <el-switch v-model="isTop" @change="onTopChange" :disabled="loading.top"></el-switch>
        </div>
        <div class="switch-item">
          <div class="label">
            <i class="el-icon-warning"></i>
            <span>加入黑名单</span>
          </div>
          <el-switch v-model="isInBlacklist" @change="onBlacklistChange" :disabled="loading.blacklist"></el-switch>
        </div>
      </div>

      <!-- 好友信息/备注设置卡片 -->
      <div class="friend-info-section-card">
        <div class="info-item">
          <div class="info-label">
            <i class="el-icon-edit"></i>
            <span>备注名</span>
          </div>
          <div class="info-content">
            <el-input
              v-if="isEditingRemark"
              v-model="remarkValue"
              size="small"
              placeholder="请输入备注名"
              maxlength="32"
              show-word-limit
              @blur="saveRemark"
              @keyup.enter.native="saveRemark"
              ref="remarkInput"
            />
            <div v-else class="info-display" @click="startEditRemark">
              <span v-if="friend.remarkNickName">{{ friend.remarkNickName }}</span>
              <span v-else class="info-placeholder">点击设置备注名</span>
              <i class="el-icon-edit-outline"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- 操作按钮区域 -->
      <div class="btn-group">
        <div class="text-btn" @click="onCleanMessage">清空聊天记录</div>
        <div class="text-btn danger" @click="onDeleteFriend">删除好友</div>
      </div>

      <div class="complaint-tip" @click="onComplaint">
        <i class="el-icon-warning-outline"></i>
        <span>被骚扰了？投诉该用户</span>
      </div>
    </div>

    <complaint ref="complaint" />
  </div>
</template>

<script>
import HeadImage from "../common/HeadImage.vue";
import Complaint from "../common/Complaint.vue";

export default {
  name: "ChatPrivateSide",
  components: {
    HeadImage,
    Complaint
  },
  props: {
    userInfo: {
      type: Object,
      default: () => ({})
    },
    chat: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      isDnd: false,
      isTop: false,
      isInBlacklist: false,
      isEditingRemark: false,
      remarkValue: "",
      // 防重复点击/加载状态标记
      loading: {
        dnd: false,
        top: false,
        blacklist: false
      }
    };
  },
  computed: {
    friend() {
      return this.friendStore?.findFriend?.(this.userInfo?.id) || {};
    }
  },
  watch: {
    userInfo: {
      handler(val) {
        if (!val) return;
        this.isDnd = !!this.friend.isDnd;
        this.isTop = !!this.friend.isTop;
        this.isInBlacklist = !!val.isInBlacklist;
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    // 免打扰开关变化
    async onDndChange(value) {
      this.loading.dnd = true;
      try {
        await this.$http({
          url: "/friend/dnd",
          method: "PUT",
          data: { friendId: this.userInfo.id, isDnd: value }
        });
        this.friendStore?.setDnd(this.userInfo.id, value);
        this.chatStore?.setDnd(this.chat, value);
        this.$message.success(value ? "已开启免打扰" : "已关闭免打扰");
      } catch (err) {
        this.$message.error("操作失败，请重试");
        this.isDnd = !value;
      } finally {
        this.loading.dnd = false;
      }
    },

    // 置顶开关变化
    async onTopChange(value) {
      this.loading.top = true;
      try {
        await this.$http({
          url: "/friend/top",
          method: "PUT",
          data: { friendId: this.userInfo.id, isTop: value }
        });
        this.friendStore?.setTop(this.userInfo.id, value);
        this.chatStore?.setTop(this.chat, value);
        this.$message.success(value ? "已置顶聊天" : "已取消置顶");
      } catch (err) {
        this.$message.error("操作失败，请重试");
        this.isTop = !value;
      } finally {
        this.loading.top = false;
      }
    },

    // 拉黑开关变化
    onBlacklistChange(value) {
      if (value) {
        this.$confirm(
          `您将不再收到对方的消息，确认将 '${this.userInfo.nickName}' 加入黑名单吗？`,
          "加入黑名单",
          {
            confirmButtonText: "确定",
            cancelButtonText: "取消",
            type: "warning"
          }
        )
          .then(() => this.updateBlacklistSetting(true))
          .catch(() => {
            this.isInBlacklist = false;
          });
      } else {
        this.updateBlacklistSetting(false);
      }
    },

    // 更新黑名单设置
    async updateBlacklistSetting(value) {
      this.loading.blacklist = true;
      const isAdd = value;
      const url = isAdd ? `/blacklist/add?userId=${this.userInfo.id}` : `/blacklist/remove?userId=${this.userInfo.id}`;
      const method = isAdd ? "POST" : "DELETE";

      try {
        await this.$http({ url, method });
        this.isInBlacklist = value;
        if (this.userInfo) this.userInfo.isInBlacklist = value;
        this.$message.success(isAdd ? `已将 '${this.userInfo.nickName}' 加入黑名单` : `已解除 '${this.userInfo.nickName}' 的黑名单`);
      } catch (err) {
        this.$message.error("操作失败，请重试");
        this.isInBlacklist = !value;
      } finally {
        this.loading.blacklist = false;
      }
    },

    // 清空聊天记录
    onCleanMessage() {
      this.$confirm(`确定要清空与 '${this.userInfo.nickName}' 的所有聊天记录吗？`, "清空聊天记录", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      }).then(() => {
        this.chatStore?.cleanMessage(this.chat);
        this.$message.success(`已清空聊天记录`);
      }).catch(() => {});
    },

    // 开始编辑备注
    startEditRemark() {
      this.isEditingRemark = true;
      this.remarkValue = this.friend.remarkNickName || "";
      this.$nextTick(() => {
        this.$refs.remarkInput?.focus();
      });
    },

    // 保存备注
    async saveRemark() {
      const newRemark = this.remarkValue.trim();
      if (newRemark === (this.friend.remarkNickName || "")) {
        this.isEditingRemark = false;
        return;
      }

      try {
        const res = await this.$http({
          url: "/friend/update/remark",
          method: "PUT",
          data: { friendId: this.userInfo.id, remarkNickName: newRemark }
        });
        this.friendStore?.updateFriend(res);
        this.chatStore?.updateChatFromFriend(res);
        this.$message.success(newRemark ? "备注设置成功" : "备注已清除");
      } catch (err) {
        this.$message.error("备注更新失败");
      } finally {
        this.isEditingRemark = false;
      }
    },

    // 删除好友
    onDeleteFriend() {
      this.$confirm(`确定要删除好友 '${this.userInfo.nickName}' 吗？删除后将无法恢复。`, "删除好友", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      }).then(async () => {
        try {
          await this.$http({
            url: `/friend/delete/${this.userInfo.id}`,
            method: "DELETE"
          });
          this.friendStore?.removeFriend(this.userInfo.id);
          this.chatStore?.removePrivateChat(this.userInfo.id);
          this.$message.success(`已删除好友 '${this.userInfo.nickName}'`);
          this.$emit("close");
        } catch (err) {
          this.$message.error("删除失败，请重试");
        }
      }).catch(() => {});
    },

    // 投诉用户
    onComplaint() {
      this.$refs.complaint?.open(1, this.userInfo.id, this.userInfo.nickName);
    },

    // 复制ID（兼容 HTTP/安全限制环境）
    copyUserName() {
      const text = this.userInfo.chatNo;
      if (!text) return;

      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text)
          .then(() => this.$message.success(`ID已复制到剪贴板`))
          .catch(() => this.fallbackCopyText(text));
      } else {
        this.fallbackCopyText(text);
      }
    },

    // 复制降级方案
    fallbackCopyText(text) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand("copy");
        if (successful) {
          this.$message.success("ID已复制到剪贴板");
        } else {
          this.$message.error("复制失败，请手动复制");
        }
      } catch (err) {
        this.$message.error("复制失败，请手动复制");
      } finally {
        document.body.removeChild(textArea);
      }
    }
  }
};
</script>

<style scoped lang="scss">
.chat-private-side {
  padding: 12px;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;

  // 抽出统一的通用卡片基础样式
  %card-style {
    padding: 12px 16px;
    background: #ffffff;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
  }

  .friend-info-section {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .friend-card {
      @extend %card-style;
      display: flex;
      align-items: flex-start;
      gap: 16px;
      padding: 16px;

      .friend-avatar {
        flex-shrink: 0;
      }

      .friend-details {
        flex: 1;
        min-width: 0; // 解决 flex 子项内部内容超出时的挤压溢出

        .friend-name-row {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 4px;

          .friend-name {
            margin: 0;
            font-size: var(--im-font-size-larger, 16px);
            font-weight: 600;
            color: var(--im-text-color, #333);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .gender-icons {
            display: inline-flex;

            .gender-icon {
              font-size: 14px;
              border-radius: 50%;
              padding: 2px;

              &.male {
                color: #1890ff;
                background: rgba(24, 144, 255, 0.1);
              }

              &.female {
                color: #f5222d;
                background: rgba(245, 34, 45, 0.1);
              }
            }
          }
        }

        .friend-company-row {
          font-size: 12px;
          color: var(--im-color-primary, #409eff);
          margin-bottom: 8px;
          word-break: break-all;
        }

        .friend-meta-list {
          display: flex;
          flex-direction: column;
          gap: 4px;

          .meta-item {
            display: flex;
            align-items: center;
            font-size: 12px;
            color: var(--im-text-color-light, #999);
            line-height: 1.4;

            .meta-label {
              margin-right: 4px;
              flex-shrink: 0;
            }

            .meta-value {
              word-break: break-all;
            }

            .copy-btn {
              font-size: 13px;
              color: var(--im-color-primary, #409eff);
              cursor: pointer;
              margin-left: 6px;
              transition: transform 0.2s ease;

              &:hover {
                transform: scale(1.15);
              }
            }
          }
        }
      }
    }

    .personal-setting {
      @extend %card-style;

      .switch-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 10px 0;
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);

        &:last-child {
          border-bottom: none;
        }

        .label {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 14px;
          color: var(--im-text-color-primary, #333);

          i {
            color: var(--im-color-primary, #409eff);
            font-size: 16px;
            width: 18px;
            text-align: center;
          }
        }
      }
    }

    .friend-info-section-card {
      @extend %card-style;

      .info-item {
        .info-label {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 6px;
          font-size: 13px;
          color: var(--im-text-color-primary, #333);

          i {
            color: var(--im-color-primary, #409eff);
            font-size: 14px;
          }
        }

        .info-content {
          .info-display {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 6px 8px;
            cursor: pointer;
            border-radius: 4px;
            transition: background 0.2s;

            &:hover {
              background: rgba(0, 0, 0, 0.04);
            }

            span {
              font-size: 13px;
              color: var(--im-text-color, #333);

              &.info-placeholder {
                color: #b0b0b0;
                font-style: italic;
              }
            }

            i {
              color: #999;
              font-size: 12px;
            }
          }
        }
      }
    }

    .btn-group {
      @extend %card-style;
      padding: 0;

      .text-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        padding: 12px;
        font-size: 14px;
        cursor: pointer;
        transition: background 0.2s ease;
        color: var(--im-text-color-primary, #333);
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);

        &:last-child {
          border-bottom: none;
        }

        &:hover {
          background: rgba(0, 0, 0, 0.03);
        }

        &.danger {
          color: var(--im-color-danger, #f56c6c);
        }
      }
    }

    .complaint-tip {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      padding: 6px 0;
      font-size: 12px;
      color: #909399;
      cursor: pointer;
      transition: color 0.2s;

      &:hover {
        color: var(--im-color-primary, #409eff);
      }
    }
  }
}
</style>